import { CSSProperties, ReactNode } from 'react';

/**
 * AlmaNote — accent-tinted card carrying Alma's voice: optional avatar +
 * gold sparkles label row, then a serif quote. Quote marks auto-added.
 * Used for "Why Alma chose <host>" on profiles and the shorter unlabelled
 * note inside host-choice cards.
 */
export interface AlmaNoteProps {
  /** Quote text WITHOUT surrounding quotation marks (added automatically). */
  quote: string;
  /** Gold label row, e.g. "Why Alma chose Matheus". Omit for the bare note. */
  label?: string;
  /** Alma's photo. */
  avatarUrl?: string;
  /** Custom avatar node (overrides avatarUrl). */
  avatar?: ReactNode;
  /** Serif quote size in px. Default 15.5 (host-choice cards use 13.5). */
  quoteSize?: number;
  style?: CSSProperties;
}

export function AlmaNote({ quote, label, avatarUrl, avatar, quoteSize = 15.5, style }: AlmaNoteProps) {
  const avatarNode = avatar ?? (avatarUrl ? (
    <img
      src={avatarUrl}
      alt="Alma"
      style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '0.5px solid rgb(var(--cream) / .18)' }}
    />
  ) : null);

  const body = (
    <div style={{ minWidth: 0 }}>
      {label ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
          <i className="ti ti-sparkles" style={{ color: 'rgb(var(--gold))', fontSize: 11 }} />
          <div style={{ color: 'rgb(var(--gold))', fontSize: 10.5, fontWeight: 500, fontFamily: 'var(--font-sans)' }}>{label}</div>
        </div>
      ) : null}
      <div style={{ fontFamily: 'var(--font-serif)', fontSize: quoteSize, lineHeight: label ? 1.58 : 1.6, color: 'var(--text)', textWrap: 'pretty' }}>
        &ldquo;{quote}&rdquo;
      </div>
    </div>
  );

  return (
    <div
      style={{
        background: 'var(--accent-tint)', border: '1px solid var(--accent-border)',
        borderRadius: label ? 12 : 10, padding: label ? '20px 22px' : '16px 18px',
        display: 'flex', gap: 14, alignItems: 'flex-start',
        ...style,
      }}
    >
      {avatarNode}
      {body}
    </div>
  );
}

export default AlmaNote;
